import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import U2000ActionBtn from '../../../u2000Core/components/buttons/U2000ActionBtn';
import U2000InputTextBase from '../../../u2000Core/components/formControls/input/U2000InputTextBase';
import { NS } from '../../U3146';
import { U3146_MAX_INACTIVE_DAYS, U3146_MIN_INACTIVE_DAYS } from '../../U3146Service';

export type U3146CloseInactiveWizardParamsPanelProps = {
  inactiveDays: number;
  onInactiveDaysChange: (value: number) => void;
  onSaveDefault?: () => void;
  onContinue: () => void;
  canSaveDefault: boolean;
  loadingPreview: boolean;
  loadingSaveDefault: boolean;
  disabled: boolean;
};

function u3146ClampInactiveDaysInput(raw: string | number): number {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) {
    return U3146_MIN_INACTIVE_DAYS;
  }
  return Math.min(U3146_MAX_INACTIVE_DAYS, Math.max(U3146_MIN_INACTIVE_DAYS, Math.floor(n)));
}

const U3146CloseInactiveWizardParamsPanel: React.FC<U3146CloseInactiveWizardParamsPanelProps> = ({
  inactiveDays,
  onInactiveDaysChange,
  onSaveDefault,
  onContinue,
  canSaveDefault,
  loadingPreview,
  loadingSaveDefault,
  disabled,
}) => {
  const { t } = useTranslation();

  const handleInactiveDaysChange = useCallback(
    (value: string | number) => {
      onInactiveDaysChange(u3146ClampInactiveDaysInput(value));
    },
    [onInactiveDaysChange],
  );

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      <div
        className={[
          'w-40 min-w-40 max-w-xs',
          '[&_input[type=number]]:[appearance:textfield]',
          '[&_input[type=number]::-webkit-inner-spin-button]:appearance-none',
          '[&_input[type=number]::-webkit-outer-spin-button]:appearance-none',
        ].join(' ')}
      >
        <U2000InputTextBase
          fieldName="inactiveDays"
          label={t('lblInactiveDays', { ns: NS })}
          type="number"
          value={inactiveDays}
          setValue={handleInactiveDaysChange}
          disabled={disabled}
          onBlur={() => onInactiveDaysChange(u3146ClampInactiveDaysInput(inactiveDays))}
        />
        <p className="mt-1 text-xs text-gray-500">
          {t('lblInactiveDaysHint', {
            ns: NS,
            min: U3146_MIN_INACTIVE_DAYS,
            max: U3146_MAX_INACTIVE_DAYS,
          })}
        </p>
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        {canSaveDefault && onSaveDefault && (
          <U2000ActionBtn
            variant="secondary"
            label={
              loadingSaveDefault
                ? t('wizardSaveDefaultLoading', { ns: NS })
                : t('btnSaveInactiveDaysDefault', { ns: NS })
            }
            handleClick={onSaveDefault}
            disabled={disabled || loadingSaveDefault || loadingPreview}
          />
        )}
        <U2000ActionBtn
          label={loadingPreview ? t('wizardContinueLoading', { ns: NS }) : t('wizardContinue', { ns: NS })}
          handleClick={onContinue}
          disabled={disabled || loadingPreview || loadingSaveDefault}
        />
      </div>
    </div>
  );
};

export default U3146CloseInactiveWizardParamsPanel;
